import {
  OptroAccessFailure,
  OptroAccessKind,
} from "../models/audit/optroAccess";

// Copy for the page shown in place of a control / work step (or a session of
// one) that Optro view access keeps off screen.

type GuardedEntity = "control" | "workStep";

const entityFor = (kind: OptroAccessKind): GuardedEntity => {
  switch (kind) {
    case "control":
    case "control-session":
    case "collab-control-session":
      return "control";
    case "work-step":
    case "work-step-session":
    case "collab-work-step-session":
      return "workStep";
  }
};

const NOUN: Record<GuardedEntity, string> = {
  control: "control",
  workStep: "work step",
};

const LIST: Record<GuardedEntity, { path: string; label: string }> = {
  control: { path: "/audit/controls/v2", label: "Back to Controls" },
  workStep: { path: "/audit/work-steps", label: "Back to Work Steps" },
};

export const OPTRO_ACCESS_SIGN_IN_ACTION = "Sign in with Optro";
export const OPTRO_ACCESS_RETRY_ACTION = "Try again";
export const OPTRO_ACCESS_CHECKING_LABEL = "Checking your Optro access";

// The list pages' notice when the backend couldn't check Optro access and so
// left every Optro-linked row out.
export const OPTRO_ACCESS_LIST_UNAVAILABLE_COPY =
  "We couldn't check your Optro access, so controls and work steps linked to Optro are hidden for now.";

export interface OptroAccessCopy {
  title: string;
  description: string;
}

export const optroAccessCopy = (
  kind: OptroAccessKind,
  failure: OptroAccessFailure,
): OptroAccessCopy => {
  const noun = NOUN[entityFor(kind)];
  switch (failure) {
    case "signInRequired":
      return {
        title: `Sign in with Optro to view this ${noun}`,
        description: `This ${noun} is linked to Optro, so only people signed in with Optro can view it. Signing in with Optro signs you out of your current session first.`,
      };
    case "denied":
      return {
        title: `You don't have permission to view this ${noun} in Optro`,
        description: `Ask your Optro administrator for access to this ${noun}.`,
      };
    case "unavailable":
      return {
        title: "We couldn't check your Optro access",
        description: `Midship couldn't reach Optro to confirm you can view this ${noun}. Try again in a few minutes.`,
      };
    case "notFound":
      return {
        title: `This ${noun} doesn't exist`,
        description: `It may have been deleted, or it belongs to a different workspace.`,
      };
  }
};

export const optroAccessListLink = (kind: OptroAccessKind) =>
  LIST[entityFor(kind)];
